import React from 'react';
import { StyleSheet, View } from 'react-native';

import { NumericField, SkeletonRow, Text } from '~/components/core';
import { formatNumber } from '~/lib/format';
import { useStyles } from '~/hooks';
import i18n from '~/i18n';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { CreateStrategyWizardApi } from '../../../hooks/useCreateStrategyWizard';
import MarketPriceRow from '../MarketPriceRow';

type Props = {
    wizard: CreateStrategyWizardApi;
};

// Step 2: size and exits — quantity and TP/SL distances, with the instrument's ranges as hints
// and live est. profit/loss. The fund is not asked for: the backend derives it (2026-07-26),
// which also makes the instrument's own quantity range the only ceiling to respect.
export default function StepTradeSetup({ wizard }: Props) {
    const styles = useStyles(stylesGenerator);
    const info = wizard.instrumentInfo;
    // Shown in both branches: the price arrives before the instrument's ranges do.
    const priceRow = wizard.instrument ? <MarketPriceRow symbol={wizard.instrument} side={wizard.side} /> : null;

    if (!info) {
        return (
            <View style={styles.loading}>
                {priceRow}
                <SkeletonRow height={54} />
                <SkeletonRow height={54} />
                <SkeletonRow height={54} />
            </View>
        );
    }

    const range = wizard.side === 'buy' ? info.buy : info.sell;
    const minQty = Math.abs(Number(range.minQuantity));
    const maxQty = Math.abs(Number(range.maxQuantity));
    const qtyStep = Math.pow(10, -info.quantityPrecision);
    const distanceStep = Math.pow(10, -info.distancePrecision);
    const hasEstimate = Number.isFinite(wizard.quantityMagnitude) && wizard.quantityMagnitude > 0;

    return (
        <View style={styles.fields}>
            {priceRow}
            <NumericField
                label={i18n.t('quantity')}
                value={wizard.quantityMagnitude}
                onChange={wizard.setQuantityMagnitude}
                min={minQty}
                max={maxQty}
                step={qtyStep}
                precision={info.quantityPrecision}
                hint={`${minQty} – ${maxQty}`}
            />
            <NumericField
                label={i18n.t('take-profit')}
                value={wizard.takeProfitDistance}
                onChange={wizard.setTakeProfitDistance}
                min={Number(info.takeProfitDistance.minValue)}
                max={Number(info.takeProfitDistance.maxValue)}
                step={distanceStep}
                precision={info.distancePrecision}
                hint={`${info.takeProfitDistance.minValue} – ${info.takeProfitDistance.maxValue}`}
                helper={
                    hasEstimate ? (
                        <Text noTranslate style={styles.estProfit}>
                            {`${i18n.t('ai-strategy-est-profit')}: +${formatNumber(
                                wizard.takeProfitDistance * wizard.quantityMagnitude,
                                2
                            )}`}
                        </Text>
                    ) : undefined
                }
            />
            <NumericField
                label={i18n.t('stop-loss')}
                value={wizard.stopLossDistance}
                onChange={wizard.setStopLossDistance}
                min={Number(info.stopLossDistance.minValue)}
                max={Number(info.stopLossDistance.maxValue)}
                step={distanceStep}
                precision={info.distancePrecision}
                hint={`${info.stopLossDistance.minValue} – ${info.stopLossDistance.maxValue}`}
                helper={
                    hasEstimate ? (
                        <Text noTranslate style={styles.estLoss}>
                            {`${i18n.t('ai-strategy-est-loss')}: -${formatNumber(
                                wizard.stopLossDistance * wizard.quantityMagnitude,
                                2
                            )}`}
                        </Text>
                    ) : undefined
                }
            />
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        loading: {
            paddingTop: scaleSize(12),
        },
        fields: {
            gap: scaleSize(6),
        },
        estProfit: {
            fontSize: FONT_SIZE_XS,
            color: theme.text_upPrice,
            marginTop: scaleSize(4),
        },
        estLoss: {
            fontSize: FONT_SIZE_XS,
            color: theme.text_downPrice,
            marginTop: scaleSize(4),
        },
    });
